/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { BatteryCompatibilityAlert, BatteryInfoAlert } from 'components/BatteryInfo/BatteryInfoAlert';
import { DataContainer } from 'components/DataContainer';
import {
  FILTER_DELIVERY,
  getFiltersMap,
  getOEMFiltersMap,
  getStockFilterOptions,
  RangeFacetLocal,
  TextFacetLocal,
} from 'components/Filter/Filter.types';
import Loader from 'components/Loader';
import { useFetchBamCommercialFamilyCodes } from 'domains/bamCommercialFamilyCodes/BamCommercialFamilyCodes.requests';
import {
  getIamCatalogFilters,
  getLastSearchedVehicleKey,
  resetIamTextFilter,
  setInitializeIamTextFilter,
} from 'domains/catalog/Catalog.store';
import {
  createIAMSupplierReferenceKey,
  IAMLaborTimesNodeWrapper,
  OEM_BRAND_KEY,
  OTHER_BRAND_KEY,
  parseIAMReferenceKeyNumber,
} from 'domains/catalog/Catalog.types';
import { mapIamOEMPrice } from 'domains/catalog/Catalog.utils';
import { getIAMReferences, getPricesMap, getStocksMapV2, IAMReferenceLocal } from 'domains/references';
import { useFetchPrices, useFetchReferencesStocks } from 'domains/references/References.requests';
import { getIamCatalogBrandsView, SparePartsViewType } from 'domains/user';
import { isConnected } from 'domains/webSockets/WebSocket.store';
import { CompareTableState } from 'pages/CatalogPage/IAM/FullTextSearchResult/CompareTable';
import {
  compareTableInitialState,
  CompareTableModal,
} from 'pages/CatalogPage/IAM/FullTextSearchResult/CompareTableModal';
import {
  getIamDeliveryFilterOptions,
  getIamReferencesByFilters,
  getUpdatedIamRangeFilters,
  getUpdatedIAMTextFilters,
} from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/IAMFilterUtils';
import IAMPlateReferenceCard from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/PlateReferenceCard';
import {
  getFilteredMotrioReferences,
  getFilteredOtherBrandsReferences,
  shouldDisplayIAMReference,
  sortIAMReferences,
  useMotrioStockPricesLoading,
} from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCardsContainerUtils';
import { Box, CenterFlex, Empty, Flex, MarginBox, Text } from 'UI';
import { FOUND, getData, hasData, hasPrice, isLoading, LOADING, NO_DATA, NOT_FOUND, SEARCH_STATUS } from 'utils';
/* eslint-enable max-len */

export const getIamContainerLoader = () => (
  <CenterFlex>
    <Loader height={'10vh'} />
  </CenterFlex>
);

export interface ReferencesCardsProps {
  isCompatible?: boolean;
  enablePricelessFilter?: boolean;
  includePriceless?: boolean;
  onPricelessScopeChange?: (referenceNumbers: string[]) => void;
  sparePartsView: SparePartsViewType;
  laborTimesWrapper?: NO_DATA | IAMLaborTimesNodeWrapper;
  setNumOfProducts?: (num: number) => void;
  textFilters?: TextFacetLocal[];
  rangeFilters?: RangeFacetLocal[];
  setTextFilters?: (t: TextFacetLocal[]) => void;
  setRangeFilters?: (r: RangeFacetLocal[]) => void;
  nodeId?: string;
  referenceNumbers: string[];
  selectedReferences?: string[];
  isMaintenance?: boolean;
  onSelect?: (referenceNumber: string) => void;
}

const ReferenceCardsContainer = ({
  isCompatible = true,
  enablePricelessFilter = false,
  includePriceless = false,
  onPricelessScopeChange,
  sparePartsView,
  laborTimesWrapper,
  setNumOfProducts,
  textFilters,
  rangeFilters,
  setTextFilters,
  setRangeFilters,
  nodeId,
  referenceNumbers,
  selectedReferences,
  isMaintenance,
  onSelect,
}: ReferencesCardsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOEM = useSelector(getIamCatalogBrandsView) === OEM_BRAND_KEY;
  const iamFilters = useSelector(getIamCatalogFilters);
  const filtersMap = isOEM ? getOEMFiltersMap(t) : getFiltersMap(t);
  const filter = useMemo(() => {
    return isOEM ? iamFilters.oemFilters : iamFilters.otherBrandFilters;
  }, [isOEM, iamFilters]);
  const isWsConnected = useSelector(isConnected);
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const iamReferencesPayload = useMemo(() => {
    return {
      referenceNumbers,
      vehicleKey,
    };
  }, [referenceNumbers, vehicleKey]);
  const references = useSelector((state: RootState) => getIAMReferences(state, iamReferencesPayload));
  const plainReferenceNumbers = useMemo(() => referenceNumbers.map(parseIAMReferenceKeyNumber), [referenceNumbers]);
  const prices = useSelector((state: RootState) => getPricesMap(state, plainReferenceNumbers));
  const stocks = useSelector((state: RootState) => getStocksMapV2(state, plainReferenceNumbers));
  const motrioStocksPricesLoading = useMotrioStockPricesLoading(
    references.filter((r) => r.supplier === 'MOTRIO').map((ref) => ref.referenceNumber),
  );
  const stockStatuses = useMemo(() => [...stocks.values()].map((s) => s?.searchStatus), [stocks]);
  const [allReferences, setAllReferences] = useState<IAMReferenceLocal[]>([]);
  const [currentReferenceNumbers, setCurrentReferenceNumbers] = useState<string[] | undefined>(undefined);
  const [searchStatus, setSearchStatus] = useState<SEARCH_STATUS>(LOADING);
  const [someStockMissingFirstLoad, setSomeStockMissingFirstLoad] = useState(true);
  const someStockLoading = stockStatuses.some((sp) => isLoading(sp));
  const stockRequestDetails = useMemo(() => {
    return references.map((ref) => {
      return {
        referenceNumber: ref.referenceNumber,
        type: ref.type,
        quantity: 1,
        origin: ref.origin,
        supplierCode: ref.supplierCode,
        referenceSource: ref.referenceSource,
        isPrimaryStockRequest: false,
        isCertasReference: false,
        isBulkReference: false,
      };
    });
  }, [references]);

  useFetchBamCommercialFamilyCodes();
  useFetchReferencesStocks(referenceNumbers ?? [], stockRequestDetails);
  useFetchPrices(referenceNumbers ?? [], vehicleKey);

  useEffect(() => {
    if (!someStockMissingFirstLoad && !someStockLoading && filter.initialTextFilters?.size !== 0) {
      dispatch(
        setInitializeIamTextFilter({
          filterType: isOEM ? OEM_BRAND_KEY : OTHER_BRAND_KEY,
        }),
      );
    }

    // eslint-disable-next-line
  }, [dispatch, someStockMissingFirstLoad, someStockLoading, filter.initialTextFilters]);

  useEffect(() => {
    if (someStockMissingFirstLoad && stockStatuses.length > 0 && allReferences.length > 0) {
      setSomeStockMissingFirstLoad(someStockLoading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockStatuses, isWsConnected]);

  useEffect(() => {
    if (!isWsConnected && allReferences.length > 0) {
      setSomeStockMissingFirstLoad(true);
    }
  }, [isWsConnected, allReferences.length]);

  /**
   * @filteredReferences: references that will be displayed in the result page
   * @pricelessScopeReferenceNumbers: references available before applying the priceless toggle,
   *                                  used for the toggle count in the parent component
   */
  const { filteredReferences, pricelessScopeReferenceNumbers } = useMemo(() => {
    const referencesFilteredManually = getIamReferencesByFilters(allReferences, stocks, filter, filtersMap, t);

    const hasPriceData = (referenceNumber: string) => {
      const price = prices.get(referenceNumber);
      return (hasData(price) && hasPrice(sparePartsView, getData(price))) || isLoading(price);
    };

    const buildFilteredReferences = (includePricelessRefs: boolean) => {
      const motrioReferences = sortIAMReferences(
        getFilteredMotrioReferences(referencesFilteredManually, prices, stocks, includePricelessRefs),
        stocks,
      );
      const allOtherBrandsReferences = sortIAMReferences(
        getFilteredOtherBrandsReferences(referencesFilteredManually),
        stocks,
      );
      const otherBrandsReferences =
        enablePricelessFilter && !includePricelessRefs
          ? allOtherBrandsReferences.filter((ref) =>
              isOEM ? mapIamOEMPrice(ref.price, ref.currencyCode) !== undefined : hasPriceData(ref.referenceNumber),
            )
          : allOtherBrandsReferences;

      const newReferences = [...motrioReferences, ...otherBrandsReferences];
      const mktpReferences = newReferences.filter((ref) => ref.referenceSource === 'MARKETPLACE');
      const nonMktpReferences = newReferences.filter((ref) => ref.referenceSource !== 'MARKETPLACE');
      const mktpReferencesWithPrice = mktpReferences.filter((ref) => hasPriceData(ref.referenceNumber));

      return includePricelessRefs || mktpReferencesWithPrice.length === 0
        ? newReferences
        : [...nonMktpReferences, ...mktpReferencesWithPrice];
    };

    const referencesWithPriceless = buildFilteredReferences(true);
    const referencesWithoutPriceless = buildFilteredReferences(false);
    const newMktpReferencesArray = includePriceless ? referencesWithPriceless : referencesWithoutPriceless;

    return {
      filteredReferences: newMktpReferencesArray,
      pricelessScopeReferenceNumbers: referencesWithPriceless.map((fr) => fr.referenceNumber),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allReferences, enablePricelessFilter, filter, stocks, t, includePriceless, isOEM, prices]);

  const [showMoreReferences, setShowMoreReferences] = useState(false);
  const firstThreeReferences = filteredReferences.slice(0, 3);
  const displayedReferences = isMaintenance && !showMoreReferences ? firstThreeReferences : filteredReferences;
  const displayedReferenceNumbers = displayedReferences.map((reference) => reference.referenceNumber);
  const referenceNumbersKey = referenceNumbers.join('|');

  useEffect(() => {
    if (!isMaintenance) {
      return;
    }

    setShowMoreReferences(false);
  }, [isMaintenance, referenceNumbersKey]);

  useEffect(() => {
    if (isMaintenance || !textFilters || !setTextFilters || !rangeFilters || !setRangeFilters) {
      return;
    }
    const updatedTextFilters = getUpdatedIAMTextFilters(textFilters, stocks, filtersMap, filteredReferences, t);
    if (stockStatuses.length > 0 && isWsConnected) {
      const hasDeliveryFilter = !!updatedTextFilters.find((ft) => ft.id === FILTER_DELIVERY);
      const stocksFilterOptions = getStockFilterOptions('IAM', !someStockLoading);
      const deliveryFilterOptions = getIamDeliveryFilterOptions(stocksFilterOptions, stocks, filteredReferences, t);
      if (hasDeliveryFilter) {
        const updatedTextFiltersWithDelivery: TextFacetLocal[] = updatedTextFilters.map((item) =>
          item.id === FILTER_DELIVERY && deliveryFilterOptions ? deliveryFilterOptions : item,
        );
        const deliveryFilterSelection = filter.textFilters.get(FILTER_DELIVERY);
        const missingSelectedDeliveryOptions =
          deliveryFilterSelection?.filter((sel) => {
            const updatedDelivery = deliveryFilterOptions.items.find((item) => item.label === sel);
            return updatedDelivery === undefined || updatedDelivery.numberOfItems === 0;
          }) ?? [];
        if (missingSelectedDeliveryOptions.length === 1) {
          dispatch(
            resetIamTextFilter({ filterType: isOEM ? OEM_BRAND_KEY : OTHER_BRAND_KEY, filterOption: FILTER_DELIVERY }),
          );
        }
        setTextFilters(updatedTextFiltersWithDelivery);
      } else {
        setTextFilters(updatedTextFilters.concat(deliveryFilterOptions));
      }
    } else {
      setTextFilters(updatedTextFilters);
    }
    setRangeFilters(getUpdatedIamRangeFilters(rangeFilters, filtersMap, filteredReferences));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, someStockLoading, someStockMissingFirstLoad]);

  useEffect(() => {
    if (isMaintenance || !setNumOfProducts) {
      return;
    }

    setNumOfProducts?.(filteredReferences.length);
    // eslint-disable-next-line
  }, [filteredReferences.length]);

  useEffect(() => {
    onPricelessScopeChange?.(pricelessScopeReferenceNumbers);
  }, [onPricelessScopeChange, pricelessScopeReferenceNumbers]);

  useEffect(() => {
    if (referenceNumbers.join('|') !== currentReferenceNumbers?.join('|')) {
      setCurrentReferenceNumbers(referenceNumbers);
      return;
    }
    if (referenceNumbers.length === 0) {
      setSearchStatus(NOT_FOUND);
      return;
    }

    const referencesArray = references.filter((referenceToDisplay) => {
      const refNumber = referenceToDisplay.referenceNumber;
      return shouldDisplayIAMReference(referenceToDisplay, prices.get(refNumber), stocks.get(refNumber), true);
    });

    setSearchStatus(FOUND);
    setAllReferences(referencesArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, currentReferenceNumbers, motrioStocksPricesLoading, isOEM, isWsConnected, referenceNumbers]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [compareTableReferences, setCompareTableReferences] = useState<CompareTableState>(compareTableInitialState);

  const handleClickCompare = (reference: string) => {
    setIsModalOpen(true);
    setCompareTableReferences((prevState) => ({ ...prevState, firstReference: reference }));
  };

  const handleModalOnCancel = () => {
    setIsModalOpen(false);
    setCompareTableReferences(compareTableInitialState);
  };

  return (
    <DataContainer data={searchStatus} Loading={getIamContainerLoader}>
      <Flex justify={'center'}>
        {displayedReferences.length > 0 ? (
          <Flex direction={'column'}>
            {!isCompatible && (
              <>
                <BatteryInfoAlert />
                <MarginBox mt={15} />
                <BatteryCompatibilityAlert />
              </>
            )}
            {displayedReferences.map((ref, index) => (
              <IAMPlateReferenceCard
                key={`iam-ref-${createIAMSupplierReferenceKey(ref.referenceNumber, ref.supplierCode)}-${index}`}
                referenceNumber={ref.referenceNumber}
                supplierCode={ref.supplierCode}
                sparePartsView={sparePartsView}
                laborTimesWrapper={laborTimesWrapper}
                isMaintenance={isMaintenance}
                selected={
                  selectedReferences?.includes(createIAMSupplierReferenceKey(ref.referenceNumber, ref.supplierCode))
                    ? true
                    : undefined
                }
                onSelect={() => onSelect?.(createIAMSupplierReferenceKey(ref.referenceNumber, ref.supplierCode))}
                handleClickCompare={handleClickCompare}
              />
            ))}
            {isMaintenance && !showMoreReferences && filteredReferences.length > firstThreeReferences.length && (
              <Text
                type="base"
                color="clear_blue"
                hoverUnderLine
                cursor={'pointer'}
                onClick={() => setShowMoreReferences(true)}
              >
                {t('common.action.show_more_references', 'Show more references')}
              </Text>
            )}
          </Flex>
        ) : (
          <Box height={'20vh'}>
            <Empty description={t('catalog.parts.filter.no_result', 'No result for the given category.')} />
          </Box>
        )}
      </Flex>
      <CompareTableModal
        open={isModalOpen}
        compareTableReferences={compareTableReferences}
        setCompareTableReferences={setCompareTableReferences}
        onCancel={handleModalOnCancel}
        vehicleKey={vehicleKey}
        compareReferencesList={displayedReferenceNumbers}
      />
    </DataContainer>
  );
};

export default ReferenceCardsContainer;
